// TeamMemberAttendanceWrapper.jsx
/* eslint-disable react/prop-types */
import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  Avatar,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { EnhancedDayRow } from "./attendance";
import Table from "./table/index";
import AttendanceCalendar from "./AttendanceCalendar";
import TimelineDetailsDrawer from "./TimelineDetailsDrawer";

import {
  dateKey,
  getTodayRange,
  shiftDateBy,
  getWeekFromDate,
  formatDateToAPI,
} from "../utils/timeUtils";
import { useTeamMemberAttendance } from "../hooks/useTeamMemberAttendance";
import { Person } from "@mui/icons-material";
import SoftTypography from "components/SoftTypography";
import FlagLoader from "layouts/flagloader";

const TeamMemberAttendanceWrapper = ({
  user,
  onBack,
  dateRange,
  onDateRangeChange,
  reportees = [],
  isTeamView = true,
  activeView, // ← Add this prop
  setActiveView,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedDayForDetails, setSelectedDayForDetails] = useState(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  // const [activeView, setActiveView] = useState("list");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  // Use the custom hook to fetch team member's data
  const { loading, error, timelineData, refresh } = useTeamMemberAttendance(user, dateRange);

  const todayKey = dateKey(new Date());
  // Get days for current week
  const getDaysForWeek = () => {
    if (!dateRange?.start) return [];
    const startDate = new Date(dateRange.start);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      days.push(date);
    }

    return days.sort((a, b) => b - a);
  };

  const handleDayClick = (data) => {
    console.log("Day clicked:", data);
    setSelectedDayForDetails(data.date);
    setDetailsDrawerOpen(true);
  };

  const handlePrev = () => {
    onDateRangeChange({
      start: shiftDateBy(dateRange.start, -7),
      end: shiftDateBy(dateRange.end, -7),
    });
  };

  const handleNext = () => {
    onDateRangeChange({
      start: shiftDateBy(dateRange.start, 7),
      end: shiftDateBy(dateRange.end, 7),
    });
  };

  const handleDateSelect = (date) => {
    const weekRange = getWeekFromDate(date);
    onDateRangeChange(weekRange);
    setActiveView("list");
  };

  const handleRefresh = () => {
    refresh();
  };

  const days = getDaysForWeek();

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        {/* <CircularProgress /> */}
        <FlagLoader />
        <Typography sx={{ mt: 2 }}>Loading {user.name} {user.last_name}s attendance data...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error">Error: {error}</Typography>
        <IconButton onClick={refresh} sx={{ mt: 2 }}>
          Retry
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
        {user && (
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 2,
            }}
          >
            {/* Back Button */}
            <IconButton onClick={onBack} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            {/* Profile Icon */}
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "#EEF2FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #E0E7FF",
              }}
            >
              <Person sx={{ fontSize: 18 }} />
            </Box>

            {/* User Name */}
            <SoftTypography variant="h6" color="text">
              {user?.name} {user?.last_name}
            </SoftTypography>
          </Box>
        )}
      </Box>

      {/* List View */}
      {activeView === "list" && (
        <Box sx={{ p: isMobile ? 1 : 0 }}>
          <Box
            sx={{
              maxHeight: "calc(100vh - 250px)",
              overflow: "auto",
              pb: 1,
              borderRadius: "12px",
            }}
          >
            {/* Day Rows */}
            {days.map((d, index) => {
              const dayKey = dateKey(d);
              const dayTimelineData = timelineData.find((item) => item.date === dayKey);

              const isToday =
                dayKey === dateKey(new Date()) || dayTimelineData?.attendance_status == "running";

              const dateString = d.getDate().toString().padStart(2, "0");
              const weekdayName = d.toLocaleDateString("en-US", { weekday: "short" });
              const dayLabel = isToday ? "Today" : `${weekdayName} ${dateString}`;

              // Find timeline data for this day from the fetched team member data
              // const dayTimelineData = timelineData.find(
              //   (item) => item.date === d.toISOString().split("T")[0]
              // );

              const entry = {
                date: d,
                status: dayTimelineData?.status || "unknown",
                color: getStatusColor(dayTimelineData?.status),
                comment: dayTimelineData?.status_reason || "",
                checkIns: dayTimelineData?.in ? [dayTimelineData.in] : [],
                checkOuts: dayTimelineData?.out ? [dayTimelineData.out] : [],
                hoursWorked: dayTimelineData?.worked_minutes
                  ? `${Math.floor(dayTimelineData.worked_minutes / 60)}:${(
                    dayTimelineData.worked_minutes % 60
                  )
                    .toString()
                    .padStart(2, "0")}`
                  : "00:00",
                isToday,
                isCheckedIn: dayTimelineData?.attendance_status === "running",
              };

              return (
                <EnhancedDayRow
                  key={index}
                  day={dayLabel}
                  date={dateString}
                  entry={entry}
                  timelineData={timelineData} // Use team member's timeline data
                  currentTimeMinutes={0}
                  onDayClick={handleDayClick}
                  isToday={isToday}
                  currentDayData={dayTimelineData}
                  user={user} // Pass team member user
                  statusColor={entry.color}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* Table View */}
      {activeView === "tabular" && (
        <Box mt={2}>
          <Table
            startDate={dateRange.start}
            endDate={dateRange.end}
            onDayClick={handleDayClick}
            timelineData={timelineData} // Use team member's timeline data
            weekDays={days}
            selectedUser={user}
          />
        </Box>
      )}

      {/* Calendar View */}
      {activeView === "calendar" && (
        <Box sx={{ mt: 2 }}>
          <AttendanceCalendar
            onDateRangeSelect={handleDateSelect}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
            userId={user.id} // Pass user ID to fetch their calendar data
          />
        </Box>
      )}

      {/* Timeline Details Drawer */}
      <TimelineDetailsDrawer
        open={detailsDrawerOpen}
        onClose={() => {
          setDetailsDrawerOpen(false);
          setSelectedDayForDetails(null);
        }}
        selectedDate={selectedDayForDetails}
        setSelectedDate={setSelectedDayForDetails}
        user={user} // Pass the team member user
        timelineData={timelineData} // Use team member's timeline data
        isTeamView={isTeamView}
      />
    </Box>
  );
};

// Helper function to get status color
const getStatusColor = (status) => {
  if (!status) return "#6B7280";
  const statusLower = status.toLowerCase();

  switch (statusLower) {
    case "present":
    case "full":
    case "logged_in":
      return "#10B981";
    case "absent":
    case "no_reason_declared":
      return "#EF4444";
    case "half_day":
    case "half":
      return "linear-gradient(90deg, #10B981 50%, #EF4444 50%)";
    case "leave":
      return "#8B5CF6";
    case "weekend":
    case "weekend_off":
      return "#F97316";
    case "holiday":
      return "#FFD700";
    case "running":
    case "working_now":
      return "#3B82F6";
    default:
      return "#6B7280";
  }
};

export default TeamMemberAttendanceWrapper;
